import { get, put, post, del } from '@/services/http';

export interface LocationRestrictionApplyTo {
  inventory_calculation: boolean;
  reports: boolean;
  forecasting: boolean;
  purchase_orders: boolean;
}

export interface Location {
  id: number;
  name: string;
  location_id?: string;
  location_name?: string;
}

export class LocationRestriction {
  public restrictedLocations: string[] = [];
  public restrictionMode: 'ignore' | 'include_only' = 'ignore';
  public applyTo: LocationRestrictionApplyTo = {
    inventory_calculation: true,
    reports: false,
    forecasting: false,
    purchase_orders: false
  };
  public createdAt?: string;
  public updatedAt?: string;

  constructor(data: any = {}) {
    // Ensure restrictedLocations are always numbers
    const rawRestrictions = data.restricted_locations || data.restrictedLocations || [];
    this.restrictedLocations = rawRestrictions.map((id: any) => String(id));
    this.restrictionMode = data.restriction_mode || data.restrictionMode || 'ignore';
    this.applyTo = {
      inventory_calculation: true, // Always true and fixed
      reports: false,
      forecasting: false,
      purchase_orders: false
    };
    this.createdAt = data.created_at || data.createdAt;
    this.updatedAt = data.updated_at || data.updatedAt;
  }

  /**
   * Load location restrictions from API
   */
  public async load(): Promise<void> {
    try {
      const res: any = await get('/location-restrictions');
      if (res.data) {
        this.mapData(res.data);
      }
    } catch (error) {
      console.error('Error loading location restrictions:', error);
      throw error;
    }
  }

  /**
   * Save location restrictions to API
   */
  public async save(): Promise<void> {
    try {
      const data = {
        restricted_locations: this.restrictedLocations,
        restriction_mode: this.restrictionMode,
        apply_to: this.applyTo
      };
      const res: any = await put('/location-restrictions', data);
      if (res.data) {
        this.mapData(res.data);
      }
    } catch (error) {
      console.error('Error saving location restrictions:', error);
      throw error;
    }
  }

  /**
   * Add a location to restrictions
   */
  public async addLocation(locationId: string): Promise<void> {
    try {
      const res: any = await post('/location-restrictions/add', { location_id: locationId });
      if (res.data) {
        this.mapData(res.data);
      }
    } catch (error) {
      console.error('Error adding location restriction:', error);
      throw error;
    }
  }

  /**
   * Remove a location from restrictions
   */
  public async removeLocation(locationId: number): Promise<void> {
    try {
      const res: any = await del(`/location-restrictions/${locationId}`);
      if (res.data) {
        this.mapData(res.data);
      }
    } catch (error) {
      console.error('Error removing location restriction:', error);
      throw error;
    }
  }

  /**
   * Bulk update location restrictions
   */
  public async bulkUpdate(locationIds: string[], action: 'add' | 'remove'): Promise<void> {
    try {
      const res: any = await post('/location-restrictions/bulk', {
        location_ids: locationIds,
        action
      });
      if (res.data) {
        this.mapData(res.data);
      }
    } catch (error) {
      console.error('Error bulk updating location restrictions:', error);
      throw error;
    }
  }

  /**
   * Check if a location is restricted
   */
  public async checkLocationRestricted(locationId: number): Promise<boolean> {
    try {
      const res: any = await get(`/location-restrictions/check/${locationId}`);
      return res.data ? res.data.is_restricted : false;
    } catch (error) {
      console.error('Error checking location restriction:', error);
      return false;
    }
  }

  /**
   * Get restricted locations for a specific context
   */
  public async getRestrictedLocationsForContext(context: string): Promise<number[]> {
    try {
      const res: any = await get(`/location-restrictions/context/${context}`);
      return res.data ? res.data.restricted_locations : [];
    } catch (error) {
      console.error('Error getting restricted locations for context:', error);
      return [];
    }
  }

  /**
   * Map data from API response to model properties
   */
  public mapData(data: any = {}): void {
    this.restrictedLocations = data.restricted_locations || data.restrictedLocations || [];
    this.restrictionMode = data.restriction_mode || data.restrictionMode || 'ignore';
    this.applyTo = {
      inventory_calculation: data.apply_to?.inventory_calculation ?? data.applyTo?.inventory_calculation ?? true,
      reports: data.apply_to?.reports ?? data.applyTo?.reports ?? false,
      forecasting: data.apply_to?.forecasting ?? data.applyTo?.forecasting ?? false,
      purchase_orders: data.apply_to?.purchase_orders ?? data.applyTo?.purchase_orders ?? false
    };
    this.createdAt = data.created_at || data.createdAt;
    this.updatedAt = data.updated_at || data.updatedAt;
  }

  /**
   * Check if location restrictions are active
   */
  public hasActiveRestrictions(): boolean {
    return this.restrictedLocations.length > 0 && 
           Object.values(this.applyTo).some(value => value);
  }

  /**
   * Check if a specific location is restricted based on current mode
   */
  public isLocationRestricted(locationId: string): boolean {
    if (this.restrictionMode === 'ignore') {
      return this.restrictedLocations.includes(locationId);
    } else {
      return !this.restrictedLocations.includes(locationId);
    }
  }

  /**
   * Get list of contexts where restrictions are applied
   */
  public getAppliedContexts(): string[] {
    const contexts: string[] = [];
    if (this.applyTo.inventory_calculation) contexts.push('Inventory Calculations');
    if (this.applyTo.reports) contexts.push('Reports');
    if (this.applyTo.forecasting) contexts.push('Forecasting');
    if (this.applyTo.purchase_orders) contexts.push('Purchase Orders');
    return contexts;
  }

  /**
   * Add location locally (without API call)
   */
  public addLocationLocal(locationId: string): void {
    if (!this.restrictedLocations.includes(locationId)) {
      this.restrictedLocations.push(locationId);
    }
  }

  /**
   * Remove location locally (without API call)
   */
  public removeLocationLocal(locationId: string): void {
    this.restrictedLocations = this.restrictedLocations.filter(id => id !== locationId);
  }

  /**
   * Toggle location restriction locally
   */
  public toggleLocationLocal(locationId: string): void {
    if (this.restrictedLocations.includes(locationId)) {
      this.removeLocationLocal(locationId);
    } else {
      this.addLocationLocal(locationId);
    }
  }

  /**
   * Reset to default settings
   */
  public reset(): void {
    this.restrictedLocations = [];
    this.restrictionMode = 'ignore';
    this.applyTo = {
      inventory_calculation: true,
      reports: false,
      forecasting: false,
      purchase_orders: false
    };
  }

  /**
   * Validate current settings
   */
  public validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(this.restrictedLocations)) {
      errors.push('Restricted locations must be an array');
    }

    if (!['ignore', 'include_only'].includes(this.restrictionMode)) {
      errors.push('Restriction mode must be either "ignore" or "include_only"');
    }

    if (typeof this.applyTo !== 'object') {
      errors.push('Apply to must be an object');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Convert to API format
   */
  public toApiFormat(): any {
    return {
      restricted_locations: this.restrictedLocations,
      restriction_mode: this.restrictionMode,
      apply_to: this.applyTo
    };
  }

  /**
   * Create a copy of the current instance
   */
  public clone(): LocationRestriction {
    return new LocationRestriction({
      restricted_locations: [...this.restrictedLocations],
      restriction_mode: this.restrictionMode,
      apply_to: { ...this.applyTo },
      created_at: this.createdAt,
      updated_at: this.updatedAt
    });
  }
}